import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faTruck, faUser, faPhone, faWeightHanging, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors.jsx';
import { useTranslation } from 'react-i18next';
import { isPieceUnit } from '../../utils/units';

const buildStatusMeta = (statuses) => ({
    PENDING:            { label: statuses.pending,    bg: '#fef3c7', text: '#92400e' },
    SUPERVISOR_ASSIGNED:{ label: statuses.assigned,   bg: '#ede9fe', text: '#6d28d9' },
    LOADING:            { label: statuses.loading,    bg: '#dbeafe', text: '#1d4ed8' },
    LOADED:             { label: statuses.loaded,     bg: '#d1fae5', text: '#065f46' },
    IN_TRANSIT:         { label: statuses.inTransit,  bg: '#e0f2fe', text: '#0369a1' },
    DELIVERED:          { label: statuses.delivered,  bg: '#f0fdf4', text: '#15803d' },
    CANCELLED:          { label: statuses.cancelled,  bg: '#fee2e2', text: '#991b1b' },
});

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const STATUS_META = buildStatusMeta(t('salesAnalytics.mySubOrders.statuses', { returnObjects: true }));
    const m = STATUS_META[status] || { label: status ?? t('common.symbols.emDash'), bg: '#f3f4f6', text: '#374151' };
    return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: m.bg, color: m.text }}>
            {m.label}
        </span>
    );
};

const fmt = (val, emDash) => {
    const n = parseFloat(val);
    if (!val || isNaN(n)) return emDash;
    return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const InfoChip = ({ icon, label, value }) => {
    const { t } = useTranslation();
    return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.MAIN }}>
        <FontAwesomeIcon icon={icon} className="text-xs flex-shrink-0" style={{ color: UI_COLORS.TEXT.MUTED }} />
        <div>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>{label}</p>
            <p className="text-sm font-bold" style={{ color: value ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED }}>
                {value || t('common.symbols.emDash')}
            </p>
        </div>
    </div>
    );
};

const MySubOrderDetail = ({ row, detail, loading, onClose }) => {
    const { t } = useTranslation();
    const strings = t('salesAnalytics.mySubOrders.detail', { returnObjects: true });
    const kg = t('salesAnalytics.mySubOrders.kg');
    const emDash = t('common.symbols.emDash');

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!row) return null;

    const data = detail ?? row;
    const items = detail?.items ?? [];

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <p className="font-black text-base" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {data.party_name ?? data.customer_name ?? data.tally_name ?? strings.orderDetail}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {data.sub_order_number ?? data.order_number ?? ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusBadge status={data.status} />
                        <button onClick={onClose} className="p-2 rounded-lg ml-1" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {/* Key info chips */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <InfoChip icon={faUser} label={strings.supervisor} value={data.supervisor_name} />
                        {data.supervisor_mobile && (
                            <InfoChip icon={faPhone} label={strings.mobile} value={data.supervisor_mobile} />
                        )}
                        <InfoChip icon={faTruck} label={strings.vehicle} value={data.vehicle_number} />
                        <InfoChip icon={faWeightHanging} label={strings.totalWeight}
                            value={fmt(data.total_weight ?? data.weight, emDash) !== emDash ? `${fmt(data.total_weight ?? data.weight, emDash)} ${kg}` : null}
                        />
                        {(data.challan_number) && (
                            <InfoChip icon={faBoxOpen} label={strings.challan} value={data.challan_number} />
                        )}
                    </div>

                    {/* Items breakdown */}
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {strings.items}
                        </h3>

                        {loading ? (
                            <div className="flex items-center justify-center py-8 gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                <span className="text-sm">{strings.loadingItems}</span>
                            </div>
                        ) : items.length === 0 ? (
                            <p className="text-sm text-center py-6" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.noItems}</p>
                        ) : (
                            <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.MAIN, 0.8) }}>
                                            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.headers.product}</th>
                                            <th className="px-4 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.headers.ordered}</th>
                                            <th className="px-4 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.headers.actual}</th>
                                            <th className="px-4 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>{strings.headers.pieces}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                        {items.map((it, idx) => (
                                            <tr key={it.id ?? idx} className="hover:bg-gray-50/60">
                                                <td className="px-4 py-3">
                                                    <p className="font-semibold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                        {it.size_name ?? it.product_name ?? emDash}
                                                    </p>
                                                    {it.load_by_pieces && (
                                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">{strings.loadByPieces}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                    {/* Ordered must be in the same unit as Actual beside it. For a pcs
                                                        line `quantity` is the optional kg weight and the ordered COUNT is
                                                        quantity_in_pieces — showing the kg here made every pcs delivery
                                                        look short by the per-piece weight. */}
                                                    {isPieceUnit(it.unit)
                                                        ? `${it.quantity_in_pieces ?? it.order_item_quantity ?? emDash} ${it.unit ?? ''}`
                                                        : `${it.order_item_quantity ?? it.quantity ?? emDash} ${it.unit ?? ''}`}
                                                </td>
                                                <td className="px-4 py-3 text-center font-semibold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                    {/* A pcs line is counted, not weighed: its actual is the delivered
                                                        PIECE count (actual_quantity), so don't show the kg actual_weight
                                                        under a "pcs" unit. kg/m lines keep the weight-first display. */}
                                                    {isPieceUnit(it.unit)
                                                        ? `${it.actual_quantity ?? it.actual_pieces ?? emDash} ${it.unit ?? ''}`
                                                        : `${it.actual_weight ?? it.actual_quantity ?? emDash} ${it.unit ?? ''}`}
                                                </td>
                                                <td className="px-4 py-3 text-center font-bold text-blue-600">
                                                    {it.actual_pieces ?? it.quantity_in_pieces ?? emDash}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MySubOrderDetail;
